import { useState, useEffect, useMemo, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  DialogActions,
  Typography,
  Autocomplete,
  ListItemText,
  Box,
  Grid,
  Divider,
  ListItem,
  Stack,
  Tooltip,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@mui/material";
import dayjs from "dayjs";
import { ClockIcon, MobileDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { SchedulerAPI } from "./config";
import { describeConfirmationEmail } from "./notificationMessages";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import AsyncAutocomplete from "../../components/autocomplete/autocomplete";
import AsyncAutocompleteAddClient from "../../components/autocompleteAddClient/autocompleteAddClient";
import stringToColor from "string-to-color";
import { toast } from "react-hot-toast";
import { baseAxios, Context } from "../../utils/config";
import { worksAtLocation } from "../../utils/locationHelpers";
import { ScheduleEntryStatus, ScheduleEntryStatusMap } from "./DetailedItem";
import notify from "devextreme/ui/notify";
import { InfoOutlined } from "@mui/icons-material";
import { useAuth } from "../../contexts/auth";
import { canManagePaymentMethod } from "../../utils/roleHelpers";
import { PAYMENT_METHODS } from "../../utils/paymentMethods";
import {
  buildServiceBusyTimes,
  findTimeOffOverlap,
  isBlockingTimeRange,
  STYLIST_AVAILABILITY_ERROR,
  STYLIST_TIME_OFF_ERROR,
  validateStylistAvailabilityForAppointment,
} from "./availabilityUtils";
import { getAppointmentTimeRange, SCHEDULER_TIMEZONE } from "./config";
import { colors } from "../../theme/colors";
import { useThemeMode } from "../../contexts/theme-mode";
import { ClientNotesList } from "../../components/client-notes/clientNotes";
import {
  primaryBtnSx,
  secondaryBtnSx,
  goldRadioSx,
  useSchedulerSurface,
} from "./schedulerDialogStyles";

dayjs.extend(utc);
dayjs.extend(timezone);

export default ({ scheduler, dataset, refreshData, onAppointmentSaved }) => {
  const surfaceRef = useSchedulerSurface();
  const { isDark } = useThemeMode();
  const { user } = useAuth();
  const isEditMode = Boolean(scheduler.edited);
  const canEditPaymentMethod = canManagePaymentMethod(user?.userRoles);
  // JD-293: Read location from global Context instead of dead localStorage
  const { location } = useContext(Context);
  const urlLocation = useLocation();
  const queryParams = new URLSearchParams(urlLocation.search);
  const locationIdFromURL = queryParams.get("location_id");
  const clientIdFromURL = queryParams.get("client_id");
  const navigate = useNavigate();
  const [userToServiceData, setUserToServiceData] = useState([]);
  const [filteredServicesOptions, setFilteredServicesOptions] = useState([]);
  const [loadingPastAppointments, setLoadingPastAppointments] = useState(false);

  const [pastAppointments, setPastAppointments] = useState([]);
  const [clientNotes, setClientNotes] = useState([]);
  const [loadingClientNotes, setLoadingClientNotes] = useState(false);

  const isOftenLate = useMemo(() => {
    const sample = pastAppointments.slice(0, 20);
    const lateCount = sample.filter((appointment) => appointment.isLate);

    return lateCount.length > 5;
  }, [pastAppointments]);

  const didNotShowToPastAppointments = useMemo(() => {
    const sample = pastAppointments.slice(0, 20);
    const noShowCount = sample.filter(
      (appointment) => appointment.status === ScheduleEntryStatus.NoShow
    );
    return noShowCount.length > 0;
  }, [pastAppointments]);

  // Stylist→service mappings (step minutes + price level) for the whole organization.
  const stylistMappingsAPI = "/services/stylistMappings";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resServices = await baseAxios.get(stylistMappingsAPI);
        const servicesData = resServices.data;
        const normalizedData = Array.isArray(servicesData)
          ? servicesData
          : servicesData.user_to_services || [];
        setUserToServiceData(normalizedData);
      } catch (e) {
        console.log(e);
      }
    };
    fetchData();
  }, []);

  const resetFilteredServices = (id) => {
    const stylistServicesIds = userToServiceData
      .filter(Boolean)
      .filter((mapping) => mapping?.user_id?.toString?.() === id)
      .map((mapping) => mapping.service_id)
      .filter(Boolean);

    const filteredServices = (dataset.services || [])
      .filter(Boolean)
      .filter((service) =>
        stylistServicesIds.includes(service.service_id?.toString?.())
      );

    setFilteredServicesOptions(
      filteredServices?.length < 1
        ? [{ title: "Add new service", service_id: -1 }]
        : filteredServices
    );
  };

  useEffect(() => {
    const user = dataset.stylists?.find(
      (user) => user.user_id === scheduler.state.user_id.value
    );

    if (!user) {
      return;
    }

    resetFilteredServices(scheduler.state.user_id.value);

    handleChange(
      { user_id: scheduler.state.user_id.value, user_name: user?.user_name },
      "user"
    );
  }, [userToServiceData]);

  const schedulesData = dataset.schedules;

  const [data, setData] = useState(() => {
    let event = scheduler.edited;

    if (!event) {
      const userId = scheduler.state.user_id.value;
      const user = dataset.stylists?.find(
        (user) => user.user_id === userId
      );
      
      // If a stylist is selected from cell click, auto-select their location
      // Prefer the location being viewed when the stylist works there.
      const viewingLocation =
        location && location.location_id !== -1 ? location : null;
      const selectedLocation = user
        ? viewingLocation && worksAtLocation(user, viewingLocation.location_id)
          ? viewingLocation
          : dataset.locations?.find(loc => loc.location_id === user.location_id)
        : location;
      
      event = {
        start: scheduler.state.start.value,
        end: scheduler.state.end.value,
        title: scheduler.state.title.value,
        event_id: scheduler.state.event_id.value,
        user_id: userId,
        user_name: user?.user_name,
        location: selectedLocation || location,
      };
    }

    return event;
  });

  const isFinishedAppointment =
    data.appointment_status === ScheduleEntryStatus.Finished;
  const isServicesLocked =
    isEditMode && isFinishedAppointment && Boolean(data.hasReceipt);
  const showPaymentMethodField =
    isEditMode &&
    isFinishedAppointment &&
    data.hasReceipt &&
    canEditPaymentMethod;

  useEffect(() => {
    if (!data.client?.client_id) {
      setClientNotes([]);
      return;
    }

    handleLoadPastAppointments();

    let cancelled = false;
    setLoadingClientNotes(true);
    baseAxios
      .get(`/clients/${data.client.client_id}/notes`)
      .then((res) => {
        if (!cancelled && res.status === 200) {
          setClientNotes(res.data?.notes || []);
        }
      })
      .catch(() => {
        if (!cancelled) setClientNotes([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingClientNotes(false);
      });

    return () => {
      cancelled = true;
    };
  }, [data.client]);

  const handleLoadPastAppointments = async () => {
    setLoadingPastAppointments(true);
    try {
      const res = await baseAxios.get(
        `/scheduler/pastAppointments/${data.client.client_id}`
      );

      if (res.status === 200) {
        setPastAppointments(res.data);
      }
    } catch (error) {
      notify("Error fetching past appointments for the client", "Error", 3000);
    } finally {
      setLoadingPastAppointments(false);
    }
  };

  const getLocationFromUserId = (user_id) => {
    const selectedUser = dataset?.stylists.filter(
      (user) => user.user_id === user_id
    );
    return selectedUser?.length > 0
      ? selectedUser[0].location_name
      : "Stylist is not selected";
  };

  const handleSubmit = async () => {
    if (!data.location || data.location?.location_id === -1) {
      toast.error("Please select a location.");
      return;
    }
    if (!data.client) {
      toast.error(
        `Select a client in ${
          data.location.location_name ?? "the selected location"
        }.`
      );
      return;
    }
    if (!data.user_id) {
      toast.error(
        `Select a stylist in ${
          data.location.location_name ?? "the selected location"
        }.`
      );
      return;
    }
    if (!data.start) {
      toast.error("Select a start time for the appointment.");
      return;
    }
    if (!data.uniqueServices || data.uniqueServices.length === 0) {
      toast.error("Please choose at least one service.");
      return;
    }
    if (!data.booking_source) {
      toast.error("Please select how this appointment was made.");
      return;
    }

    let user_id = data.user_id.toString();

    const serviceIds = data.uniqueServices.map((service) => service.service_id);

    const userId = user_id;

    const serviceSteps = userToServiceData
      .filter(
        (entry) =>
          entry.user_id.toString() === userId.toString() &&
          serviceIds.includes(Number(entry.service_id))
      )
      .map((entry) => ({
        service_id: entry.service_id,
        service_step1_minutes: entry.service_step1_minutes,
        service_step2_minutes: entry.service_step2_minutes,
        service_step3_minutes: entry.service_step3_minutes,
      }));

    const servicesWithSteps = data.uniqueServices.map((service) => {
      const matchingSteps = serviceSteps.find(
        (step) => step.service_id === service.service_id.toString()
      );
      if (matchingSteps) {
        return {
          ...service,
          service_step1_minutes: matchingSteps.service_step1_minutes,
          service_step2_minutes: matchingSteps.service_step2_minutes,
          service_step3_minutes: matchingSteps.service_step3_minutes,
        };
      }
      return service;
    });

    const filteredServicesWithSteps = servicesWithSteps.filter(
      (x) =>
        x.service_step1_minutes > 0 ||
        x.service_step2_minutes > 0 ||
        x.service_step3_minutes > 0
    );

    if (filteredServicesWithSteps?.length === 0) {
      toast.error(
        "Please choose at least one service with total step duration of at least 1 minute."
      );
      return;
    }

    const newScheduleStart = dayjs(data.start).tz(SCHEDULER_TIMEZONE, true);
    const { busyTimes, appointmentEnd } = buildServiceBusyTimes(
      newScheduleStart,
      data.uniqueServices,
      serviceSteps
    );

    const selectedStylist = dataset.stylists.find(
      (s) => s.user_id === data.user_id
    );
    const availabilityCheck = validateStylistAvailabilityForAppointment(
      selectedStylist,
      newScheduleStart,
      appointmentEnd
    );
    if (!availabilityCheck.valid) {
      toast.error(availabilityCheck.message || STYLIST_AVAILABILITY_ERROR);
      return;
    }

    // Time off is a hard stop, as in the API. An appointment already inside
    // time off can still be edited as long as its stylist and start stay
    // the same; changing either is a move and cannot land in time off.
    const storedRange = scheduler.edited
      ? getAppointmentTimeRange(scheduler.edited, schedulesData)
      : null;
    const timeOffHit = findTimeOffOverlap(
      dataset.timeOff,
      data.user_id,
      newScheduleStart,
      appointmentEnd,
      storedRange
        ? {
            userId: scheduler.edited.user_id,
            start: storedRange.start,
            end: storedRange.end,
          }
        : null
    );
    if (timeOffHit) {
      toast.error(STYLIST_TIME_OFF_ERROR);
      return;
    }

    let overlapStart;

    schedulesData
      .filter(
        (x) =>
          x.appointmentId !== data.appointmentId &&
          x.appointment_status !== ScheduleEntryStatus.Canceled &&
          new Date(
            dayjs(x.start).year(),
            dayjs(x.start).month(),
            dayjs(x.start).date()
          ).getTime() ===
            new Date(
              newScheduleStart.year(),
              newScheduleStart.month(),
              newScheduleStart.date()
            ).getTime()
      )
      .forEach((schedule) => {
        if (schedule.user_id.toString() !== userId.toString()) {
          return false;
        }
        const existingStart = new Date(
          schedule.services[0].serviceStart
        ).getTime();
        const existingEnd = new Date(schedule.services[0].serviceEnd).getTime();
        if (!isBlockingTimeRange(existingStart, existingEnd)) {
          return;
        }

        busyTimes.forEach((time) => {
          const newStart = time.start.toDate().getTime();
          const newEnd = time.end.toDate().getTime();
          if (!isBlockingTimeRange(newStart, newEnd)) {
            return;
          }

          if (newStart <= existingStart && newEnd > existingStart) {
            if (
              !overlapStart ||
              existingStart < overlapStart.toDate().getTime()
            ) {
              overlapStart = dayjs(schedule.start).utc(false);
            }
          } else if (newStart > existingStart && newStart < existingEnd) {
            if (!overlapStart || schedule.start < overlapStart) {
              overlapStart = time.start;
            }
          }
        });
      });

    if (overlapStart) {
      const confirmResult = window.confirm(
        `Upon scheduling this service, the stylist will be double-booked starting at ${dayjs(
          overlapStart
        )
          .utc(true)
          .format("h:mm A")}. Would you like to proceed?`
      );

      if (!confirmResult) {
        return;
      }
    }

    const wasEdit = Boolean(scheduler.edited);
    const savedStart = data.start;
    let saved = false;

    if (wasEdit) {
      saved = await modifySubmit(filteredServicesWithSteps);
    } else {
      await createSubmit(filteredServicesWithSteps);
    }

    setData({
      client: null,
      user_id: null,
      user_name: "",
      location: null,
      uniqueServices: [],
      booking_source: "",
    });
    setFilteredServicesOptions([]);
    setUserToServiceData([]);
    await refreshData();
    if (wasEdit && saved) {
      onAppointmentSaved?.(savedStart);
    }
  };

  const createSubmit = async (filteredServicesWithSteps) => {
    let toastId;
    try {
      toastId = toast.loading("Creating appointment...");
      scheduler.loading(true);

      const res = await baseAxios.post(SchedulerAPI.create, {
        location_id: data.location.location_id,
        user_id: data.user_id,
        client_id: data.client.client_id,
        services: filteredServicesWithSteps,
        start: dayjs(data.start).format("YYYY-MM-DD HH:mm:ss"),
        booking_source: data.booking_source,
      });
      if (res.status === 200) {
        // JD-408: the API waits for the confirmation email and reports the outcome.
        const outcome = describeConfirmationEmail(res.data?.confirmationEmail);
        if (outcome.kind === "success") {
          toast.success(outcome.message);
        } else if (outcome.kind === "warning") {
          toast(outcome.message, { icon: "⚠️", duration: 5000 });
        } else {
          toast.error(outcome.message, { duration: 6000 });
        }
        return res.data;
      }
      return null;
    } catch (err) {
      console.log("error creating appointment");
      toast.error(err.response?.data?.message || "Something went wrong.");
      return null;
    } finally {
      toast.dismiss(toastId);
      scheduler.close();
      scheduler.loading(false);
    }
  };

  const modifySubmit = async (filteredServicesWithSteps) => {
    const toastId = toast.loading("Updating appointment...");
    try {
      scheduler.loading(true);

      const res = await baseAxios.put(SchedulerAPI.modify, {
        location_id: data.location.location_id,
        user_id: data.user_id,
        client_id: data.client.client_id,
        services: filteredServicesWithSteps,
        start: dayjs(data.start).format("YYYY-MM-DD HH:mm:ss"),
        schedule_entry_id: data.appointmentId,
        booking_source: data.booking_source,
        ...(showPaymentMethodField &&
        data.paymentMethod !== "" &&
        data.paymentMethod !== null &&
        data.paymentMethod !== undefined
          ? { payment_method: Number(data.paymentMethod) }
          : {}),
      });

      if (res.status === 200) {
        toast.success("Appointments updated successfully.");
        return true;
      }
      return false;
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      return false;
    } finally {
      scheduler.close();
      scheduler.loading(false);
      toast.dismiss(toastId);
    }
  };

  const handleChange = (value, name) => {
    if (name === "location") {
      setData((prev) => {
        return {
          ...prev,
          client: null,
          user_id: null,
          user_name: "",
          location: value,
        };
      });
    } else if (name === "user") {
      const selectedStylistId = value?.user_id.toString();

      resetFilteredServices(selectedStylistId);

      setData((prev) => ({
        ...prev,
        user_id: value?.user_id,
        user_name: value?.user_name || "",
        services: !scheduler.edited ? [] : prev.services,
      }));
    } else {
      setData((prev) => {
        return {
          ...prev,
          [name]: value,
        };
      });
    }
  };

  //handle default values from create client form
  useEffect(() => {
    // Set default location
    if (locationIdFromURL) {
      const foundLocation = dataset.locations.find(
        (loc) => loc.location_id.toString() === locationIdFromURL
      );
      if (foundLocation) {
        handleChange(foundLocation, "location");
      }
    }

    // Set default client
    if (clientIdFromURL) {
      const foundClient = dataset.clients.find(
        (client) => client.client_id.toString() === clientIdFromURL
      );
      if (foundClient) {
        handleChange(foundClient, "client");
      }
    }
  }, [locationIdFromURL, clientIdFromURL, dataset.locations, dataset.clients]);

  const addNewServiceToStylist = () => {
    navigate({
      pathname: "/add-service-to-Stylist",
      search: `?param=${data.user_id}`,
    });
  };

  return (
    <Box
      ref={surfaceRef}
      px={5}
      pt={4}
      pb={2}
      sx={{ minHeight: "760px", position: "relative" }}
    >
      <Grid container rowGap={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={12} sm={12} md={6}>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                mb={3}
                variant="h6"
                textAlign="center"
                fontWeight={700}
                sx={{ color: isDark ? "#f2f2f2" : colors.black }}
              >
                Make an appointment
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <AsyncAutocomplete
                label="Locations"
                datasets={
                  dataset.locations?.filter(
                    (location) => location.location_id !== -1
                  ) || []
                }
                keyField="location_id"
                titleField="location_name"
                subtitleField="organization_name"
                sortField="location_name"
                value={data.location}
                onChange={(value) => handleChange(value, "location")}
                locationIdFromURL={locationIdFromURL}
              />
            </Grid>
            <Grid item xs={12}>
              <AsyncAutocompleteAddClient
                label="Clients"
                keyField="client_id"
                titleField="client_name"
                subtitleField="org_name"
                phoneField="client_mobile_phone_number"
                emailField="client_email"
                sortField="client_name"
                value={data.client}
                onChange={(value) => handleChange(value, "client")}
                disabled={!data.location || data.location?.location_id === -1}
                selectedLocation={data.location} // Passing selected location
                clientIdFromURL={clientIdFromURL}
                onClientCreated={(newClient) => {
                  // Refresh the dataset to include the new client
                  // refreshData();
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <AsyncAutocomplete
                label="Stylists"
                datasets={
                  dataset?.stylists.filter(
                    (user) => worksAtLocation(user, data.location?.location_id)
                  ) || []
                }
                keyField="user_id"
                titleField="user_name"
                sortField="user_name"
                value={{
                  user_id: data.user_id,
                  user_name: data.user_name,
                }}
                onChange={(value) => handleChange(value, "user")}
              />
            </Grid>
            <Grid item xs={12}>
              <Tooltip
                title={
                  isServicesLocked
                    ? "Services cannot be changed after checkout."
                    : ""
                }
                placement="top"
                arrow
              >
                <Box
                  sx={
                    isServicesLocked
                      ? {
                          opacity: 0.55,
                          filter: "grayscale(100%)",
                          pointerEvents: "none",
                        }
                      : undefined
                  }
                >
                  <Autocomplete
                    multiple
                    disabled={isServicesLocked}
                    className={`autocomplete-servicefield ${
                      data.services?.length > 0 ? "success" : ""
                    }`}
                    options={filteredServicesOptions}
                    size="small"
                    filterSelectedOptions
                    defaultValue={data.uniqueServices}
                    getOptionLabel={(option) =>
                      option.title || option.service_name || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.service_id === value?.service_id
                    }
                    renderOption={(props, data) => {
                      return (
                        <ListItem {...props}>
                          {data.service_id === -1 ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                              }}
                            >
                              <ListItemText
                                primary="No Services Found"
                                secondary="Would you like to add a new service?"
                              />
                              <Button
                                variant="contained"
                                onClick={addNewServiceToStylist}
                                sx={primaryBtnSx}
                              >
                                Add Service
                              </Button>
                            </Box>
                          ) : (
                            data.service_name
                          )}
                        </ListItem>
                      );
                    }}
                    onChange={(e, value) => handleChange(value, "uniqueServices")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Services"
                        placeholder={
                          isServicesLocked
                            ? "Locked after checkout"
                            : "Select Multiple Services"
                        }
                      />
                    )}
                  />
                </Box>
              </Tooltip>
            </Grid>
            {showPaymentMethodField && (
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  className={`autocomplete-servicefield ${
                    data.paymentMethod !== "" &&
                    data.paymentMethod !== null &&
                    data.paymentMethod !== undefined
                      ? "success"
                      : ""
                  }`}
                  options={PAYMENT_METHODS}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  value={
                    PAYMENT_METHODS.find(
                      (method) => method.value === data.paymentMethod
                    ) || null
                  }
                  onChange={(e, value) =>
                    handleChange(
                      value?.value ?? "",
                      "paymentMethod"
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Method of Payment" />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} mb={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDateTimePicker
                  className={`autocomplete-datefield ${
                    data.start ? "success" : ""
                  }`}
                  slotProps={{ textField: { size: "small" } }}
                  label="Start Time"
                  format="MMM D, h:mm A"
                  minutesStep={15}
                  defaultValue={dayjs(data.start)}
                  onChange={(value) => handleChange(value, "start")}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                sx={{
                  width: "100%",
                  px: 1,
                  py: 1,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <FormLabel
                  component="legend"
                  sx={{
                    fontSize: "0.875rem",
                    color: "text.secondary",
                    px: 1,
                  }}
                >
                  How was this appointment made?
                </FormLabel>
                <RadioGroup
                  row
                  name="booking_source"
                  value={data.booking_source ?? ""}
                  onChange={(e) =>
                    handleChange(e.target.value, "booking_source")
                  }
                  sx={{
                    gap: 1.5,
                    pl: 2,
                    "& .MuiFormControlLabel-root": { mr: 0 },
                    "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                  }}
                >
                  <FormControlLabel value="walk_in" control={<Radio size="small" sx={goldRadioSx} />} label="Walk-in" />
                  <FormControlLabel value="phone" control={<Radio size="small" sx={goldRadioSx} />} label="Phone Call" />
                  <FormControlLabel value="email" control={<Radio size="small" sx={goldRadioSx} />} label="Email Request" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Grid container>
            <Grid item xs={12} mb={3}>
              <Typography variant="h6" textAlign="center" fontWeight={700}>
                Preview
              </Typography>
              {(isOftenLate || didNotShowToPastAppointments) && (
                <Box
                  sx={{
                    marginTop: "6px",
                    backgroundColor: "#ff000066",
                    borderRadius: "8px",
                    padding: "10px",
                    gap: "10px",
                  }}
                >
                  {isOftenLate && (
                    <Typography fontSize={16} textAlign="center">
                      This client is often late!
                    </Typography>
                  )}
                  {didNotShowToPastAppointments && (
                    <Typography fontSize={16} textAlign="center">
                      This client did not show up to some of the past
                      appointments!
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    color: "white",
                    // background: stringToColor(data.user_name),
                    background:
                      data.services?.[0]?.color ||
                      stringToColor(data.user_name),
                    marginRight: "10px",
                  }}
                >
                  {data.user_name?.length > 0 ? data.user_name[0] : "?"}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "14px", marginBottom: "0px" }}>
                    {data.user_name || "Not specified"}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: isDark ? "rgba(255,255,255,0.62)" : "#909090" }}>
                    {getLocationFromUserId(data.user_id)}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ marginBottom: "5px" }} />
              <Box
                sx={{
                  paddingLeft: "13px",
                  paddingBottom: "3px",
                  // background: stringToColor(data.user_name),
                  background:
                    data.services?.[0]?.color || stringToColor(data.user_name),
                }}
              >
                <Box
                  sx={{
                    paddingTop: "5px",
                    paddingLeft: "10px",
                    background: isDark ? "#141416" : "#e0e0e0",
                    height: "100%",
                    minHeight: "100px",
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      marginTop: "5px",
                    }}
                  >
                    {data.client?.client_name}
                  </Box>
                  <Box sx={{ fontSize: "12px", marginTop: "5px" }}>
                    {dayjs(data.start).format("MMM D, h:mm A")}
                  </Box>
                  <Box sx={{ fontSize: "12px", marginTop: "5px" }}>
                    {data.uniqueServices?.map((service) => (
                      <Box sx={{ marginTop: "5px" }}>
                        <Box>{service.service_name}</Box>
                        <Box>{service.service_shortcut}</Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
              <Stack
                sx={{
                  marginTop: 2,
                }}
              >
                <Typography sx={{ marginBottom: 1 }}>
                  Past appointments
                </Typography>
                {loadingPastAppointments ? (
                  "Loading Past Appointments"
                ) : (
                  <Stack
                    sx={{
                      gap: 0.5,
                      maxHeight: "120px",
                      overflowY: "auto",
                    }}
                  >
                    {pastAppointments.map((appointment) => (
                      <Stack
                        key={
                          appointment.appointmentId ??
                          `walk-in-${appointment.receiptId}`
                        }
                        sx={{
                          flexDirection: "row",
                          width: "100%",
                          color:
                            appointment.appointmentStatus ==
                            ScheduleEntryStatus.NoShow
                              ? "red"
                              : appointment.isLate ||
                                appointment.appointmentStatus ==
                                  ScheduleEntryStatus.Canceled
                              ? "orange"
                              : "",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "14px", flexGrow: 1 }}
                        >{`${appointment.stylistName} ${appointment.stylistLastName}`}</Typography>
                        {appointment.services &&
                        appointment.services.length === 1 ? (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              alignContent: "center",
                              display: "flex",
                              gap: 0.5,
                              flexGrow: 1,
                              justifyContent: "center",
                              marginBlock: "auto",
                            }}
                          >
                            {appointment.services[0].serviceName}
                            {/* <InfoOutlined
                              fontSize="12"
                              sx={{ marginBlock: "auto" }}
                            /> */}
                          </Typography>
                        ) : (
                          <Tooltip
                            title={appointment.services
                              .map((service) => service.serviceName)
                              .join(", ")}
                            placement="top"
                            arrow
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                alignContent: "center",
                                display: "flex",
                                gap: 0.5,
                                flexGrow: 1,
                                justifyContent: "center",
                                marginBlock: "auto",
                              }}
                            >
                              Services
                              <InfoOutlined
                                fontSize="12"
                                sx={{ marginBlock: "auto" }}
                              />
                            </Typography>
                          </Tooltip>
                        )}
                        <Typography sx={{ fontSize: "14px", marginRight: 1 }}>
                          {dayjs(appointment.date).format("MM/DD/YYYY")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            width: "90px",
                            textAlign: "right",
                          }}
                        >
                          {appointment.isWalkIn
                            ? "Walk-in"
                            : ScheduleEntryStatusMap[
                                appointment.appointmentStatus
                              ]}
                          {appointment.isLate && (
                            <ClockIcon
                              fontSize="12"
                              sx={{ marginLeft: "4px" }}
                            />
                          )}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              width: "100%",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              px: 2,
              py: 1.5,
              backgroundColor: "background.paper",
            }}
          >
            <Typography fontWeight={700} sx={{ mb: 1 }}>
              Notes
            </Typography>
            <ClientNotesList
              notes={data.client ? clientNotes : []}
              loading={Boolean(data.client) && loadingClientNotes}
              emptyText={
                data.client
                  ? "No notes for this client."
                  : "Select a client to see notes."
              }
              minHeight={220}
            />
          </Box>
        </Grid>
      </Grid>
      <DialogActions
        sx={{
          marginTop: "16px",
          px: 0,
          gap: 1.5,
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={scheduler.close} sx={secondaryBtnSx}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} sx={primaryBtnSx}>
          Confirm
        </Button>
      </DialogActions>
    </Box>
  );
};
